import {useEffect, useMemo} from 'react';
import './periodicity-form.scss';
import {Controller, UseFormReturn} from 'react-hook-form';
import {Box, Collapse, Slide, Stack, Typography, Tooltip, Checkbox, FormGroup} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Pallete} from '../../theme/bluebird';
import moment from 'moment';
import {getDaysOfTheWeek} from '../../utils/functions/days-of-the-week';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {FlatStartsAtDate} from '../flat-starts-at-date/flat-starts-at-date';
import {FlatStartsAtTime} from '../flat-starts-at-time/flat-starts-at-time';
import {Frequency, FrequencyType, SchedulerTypes, WeekDay} from '../../types';

export type PeriodicityFormType =
  | {
      // Expected values for activities-group-schedule
      type: {
        key: SchedulerTypes;
        label: string;
      };
      frequency: {
        key: FrequencyType;
        label: string;
      };
      frequencyValue: number;
      startDate?: string;
      days: WeekDay[];
    }
  | {
      // Expected values for activity-type-schedule
      frequency: {
        key: FrequencyType;
        label: string;
      };
      frequencyValue: number;
      endDate?: string;
      endHour?: string;
      days: WeekDay[];
    };

interface PeriodicityFormProps {
  type: SchedulerTypes.ActivityGroup | SchedulerTypes.ActivityType | SchedulerTypes.CareService;
  displayForm: boolean;
  formInstance: UseFormReturn<PeriodicityFormType, any>;
  startAtTimeValue?: string;
  startAtDateValue?: string;
}

export const PeriodicityForm = ({
  type,
  displayForm,
  startAtTimeValue,
  startAtDateValue,
  formInstance: {
    control,
    unregister,
    getValues,
    setValue,
    formState: {errors},
    watch,
    trigger,
  },
}: PeriodicityFormProps) => {
  const {t} = useTranslation();

  const repetitionOptions = useMemo(() => {
    if (type === SchedulerTypes.ActivityGroup) {
      return [
        {
          key: Frequency.Daily,
          label: t(translations.periodicity_form.fields.frequency.daily),
        },
        {
          key: Frequency.Weekly,
          label: t(translations.periodicity_form.fields.frequency.weekly),
        },
      ];
    }
    if (type === SchedulerTypes.CareService) {
      return [
        {
          key: Frequency.Daily,
          label: t(translations.periodicity_form.fields.frequency.daily),
        },
        {
          key: Frequency.Weekly,
          label: t(translations.periodicity_form.fields.frequency.weekly),
        },
        {
          key: Frequency.Monthly,
          label: t(translations.periodicity_form.fields.frequency.monthly),
        },
      ];
    }
    return [
      {
        key: Frequency.Hourly,
        label: t(translations.periodicity_form.fields.frequency.hourly),
      },
      {
        key: Frequency.Daily,
        label: t(translations.periodicity_form.fields.frequency.daily),
      },
      {
        key: Frequency.Weekly,
        label: t(translations.periodicity_form.fields.frequency.weekly),
      },
    ];
  }, [t, type]);

  const repetitionTimes = useMemo(() => {
    const frequency = getValues('frequency.key');

    if (frequency === Frequency.Hourly) {
      return {
        min: 1,
        max: 24,
      };
    }
    if (frequency === Frequency.Daily) {
      return {
        min: 1,
        max: 7,
      };
    }
    if (frequency === Frequency.Weekly) {
      return {
        min: 1,
        max: 52,
      };
    }
    if (frequency === Frequency.Monthly) {
      return {
        min: 1,
        max: 12,
      };
    }
    return {min: 1, max: 1};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues('frequency.key'), getValues]);

  watch();

  useEffect(() => {
    if (displayForm) trigger();
  }, [displayForm, trigger]);

  return (
    <>
      {/* <DevTool control={control} /> */}
      <form id={type} style={{width: '100%'}}>
        <Collapse in={displayForm}>
          <Stack className="periodicity-form" sx={{width: '100%', gap: '20px'}}>
            {type === SchedulerTypes.ActivityGroup && (
              <Stack
                flexDirection="row"
                sx={{
                  width: '100%',
                  gap: '20px',
                  alignItems: 'flex-start',
                }}
              >
                <label
                  style={{
                    width: '100px',
                    alignSelf: 'center',
                  }}
                >
                  {t(translations.periodicity_form.fields.start_at_date.label)}:
                </label>
                <Controller
                  name="startDate"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t(translations.dynamic_form.required_message),
                    },
                  }}
                  render={({field, fieldState: {error}}) => (
                    // @ts-ignore
                    <FlatStartsAtDate {...field} errors={error?.message} sx={{width: '140px'}} />
                  )}
                />
              </Stack>
            )}

            <Stack
              flexDirection="row"
              sx={{
                width: '100%',
                gap: '20px',
                justifyContent: 'flex-start',
              }}
            >
              <label
                style={{
                  width: type === SchedulerTypes.ActivityType ? '90px' : '100px',
                  alignSelf: 'center',
                  flexShrink: 0,
                }}
              >
                {t(translations.periodicity_form.fields.frequency.label)}:
              </label>
              <Controller
                name="frequencyValue"
                defaultValue={1}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t(
                      translations.periodicity_form.fields.frequency_value.validations.required
                    ),
                  },
                  min: repetitionTimes.min
                    ? {
                        value: repetitionTimes.min,
                        message: t(
                          translations.periodicity_form.fields.frequency_value.validations.min
                        ),
                      }
                    : undefined,
                  max: repetitionTimes.max
                    ? {
                        value: repetitionTimes.max,
                        message: t(
                          translations.periodicity_form.fields.frequency_value.validations.max,
                          {value: repetitionTimes.max}
                        ),
                      }
                    : 1,
                }}
                render={({field}) => {
                  return (
                    <FlatInput
                      type="number"
                      inputProps={{
                        ...repetitionTimes,
                        ...field,
                      }}
                      onIncrement={() => field.onChange(Number(field.value) + 1)}
                      onDecrement={() =>
                        field.value > 1 ? field.onChange(Number(field.value) - 1) : () => {}
                      }
                      sx={{
                        width: type === SchedulerTypes.ActivityType ? '90px' : '100px',
                        flexShrink: 0,
                      }}
                    />
                  );
                }}
              />

              <Controller
                name="frequency"
                control={control}
                defaultValue={{
                  key: Frequency.Daily,
                  label: t(translations.periodicity_form.fields.frequency.daily),
                }}
                rules={{
                  required: {
                    value: true,
                    message: t(
                      translations.periodicity_form.fields.frequency_value.validations.required
                    ),
                  },
                }}
                render={({field}) => (
                  <FlatSelectDropdown
                    {...field}
                    onChange={(e) => {
                      const frequency = e?.key;

                      field.onChange(e);

                      if (!frequency) return;

                      if (frequency === Frequency.Hourly) {
                        //@ts-ignore
                        // setValue('endDate', moment(), {shouldValidate: true});
                        unregister('days');
                      }

                      // endDate cannot be before 7 days distance
                      if (
                        frequency === Frequency.Weekly &&
                        getValues('endDate') &&
                        moment(getValues('endDate')).isBefore(moment().add(7, 'days'))
                      ) {
                        //@ts-ignore
                        setValue('endDate', moment().add(7, 'days'), {
                          shouldValidate: true,
                        });
                      }

                      // endDate cannot be before 7 days distance
                      if (frequency === Frequency.Daily) {
                        if (
                          getValues('endDate') &&
                          moment(getValues('endDate')).isBefore(moment().add(1, 'day'))
                        ) {
                          //@ts-ignore
                          setValue('endDate', moment().add(1, 'day'), {
                            shouldValidate: true,
                          });
                        }

                        unregister('days');
                      }
                    }}
                    options={repetitionOptions}
                    sx={{
                      width: '108px',
                    }}
                  />
                )}
              />
              <Slide direction="left" in={Boolean(errors?.frequency)}>
                <span style={{color: Pallete.red.base, fontSize: '14px'}}>
                  {errors?.frequency?.message}
                </span>
              </Slide>
            </Stack>
            {(type === SchedulerTypes.ActivityType || type === SchedulerTypes.CareService) && (
              <Stack
                flexDirection="row"
                gap="20px"
                sx={{
                  '& span.input-error': {bottom: '-20px'},
                }}
              >
                <label
                  style={{
                    width: type === SchedulerTypes.ActivityType ? '90px' : '100px',
                    alignSelf: 'center',
                    flexShrink: 0,
                  }}
                >
                  {t(translations.periodicity_form.fields.end_date.label)}:
                </label>
                <Controller
                  name="endDate"
                  control={control}
                  rules={{
                    required: {
                      value: type === SchedulerTypes.CareService,
                      message: t(translations.dynamic_form.required_message),
                    },
                    validate: (value) => {
                      if (startAtDateValue && value) {
                        if (moment(startAtDateValue).isAfter(value, 'day')) {
                          return t(
                            translations.periodicity_form.fields.end_date.validations
                              .superior_to_start_date
                          );
                        }
                      }
                      return true;
                    },
                  }}
                  render={({field, fieldState: {error}}) => (
                    // @ts-ignore
                    <FlatStartsAtDate
                      {...field}
                      allowClear={true}
                      required={type === SchedulerTypes.CareService}
                      timezone="UTC"
                      shouldDisableDate={(date) => {
                        if (startAtDateValue && date) {
                          if (moment(date).isBefore(startAtDateValue, 'days')) return true;
                        }
                        return false;
                      }}
                      errors={error?.message}
                      sx={{
                        width: '220px',
                        flexShrink: 0,
                      }}
                    />
                  )}
                />
              </Stack>
            )}

            <Collapse in={getValues('frequency')?.key === Frequency.Hourly} sx={{width: '100%'}}>
              <Stack flexDirection="row" gap="20px">
                <label
                  style={{
                    width: type === SchedulerTypes.ActivityType ? '90px' : '100px',
                    alignSelf: 'center',
                    flexShrink: 0,
                  }}
                >
                  {t(translations.periodicity_form.fields.end_hour.label)}:
                </label>
                <Controller
                  name="endHour"
                  disabled={!getValues('endDate')}
                  control={control}
                  rules={{
                    required: {
                      value: false,
                      message: t(translations.dynamic_form.required_message),
                    },
                  }}
                  render={({field, fieldState: {error}}) => (
                    // @ts-ignore
                    <FlatStartsAtTime
                      {...field}
                      allowClear={true}
                      shouldDisableTime={(time) => {
                        if (startAtTimeValue && startAtDateValue && time) {
                          const startAndEndDatesAreTheSame = moment(startAtDateValue).isSame(
                            moment(getValues('endDate')),
                            'days'
                          );
                          const endDateIsPreviousToTheDefinedFrequencyAmount = moment(
                            startAtTimeValue
                          )
                            .add(getValues('frequencyValue'), 'hours')
                            .isAfter(time);

                          // If the start date and the end date are on the same day, we are validating the minimum hour interval per the defined frequency amount, and disabling the past accordingly
                          if (
                            startAndEndDatesAreTheSame &&
                            endDateIsPreviousToTheDefinedFrequencyAmount
                          )
                            return true;
                        }
                        return false;
                      }}
                      errors={error?.message}
                      sx={{width: '146px'}}
                    />
                  )}
                />
              </Stack>
            </Collapse>

            <Collapse
              in={getValues('frequency')?.key === Frequency.Weekly}
              sx={{width: '100%', mt: '10px'}}
              unmountOnExit
              mountOnEnter
            >
              <Stack flexDirection="column" gap={2} sx={{width: '100%'}}>
                <label>{t(translations.periodicity_form.fields.days.label)}:</label>

                <FormGroup
                  row
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  {getDaysOfTheWeek().map((day) => (
                    <Controller
                      key={day.key}
                      name="days"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t(
                            translations.periodicity_form.fields.days.validations.required
                          ),
                        },
                      }}
                      render={({field}) => {
                        const checked = field.value?.includes(day.key) || false;

                        return (
                          <Tooltip key={day.key} title={day.label}>
                            <Box>
                              <Checkbox checked={checked} sx={{display: 'none'}} />
                              <Box
                                onClick={() => {
                                  const isChecked = !checked;
                                  const selectedDays = field.value || [];

                                  let updatedDays;

                                  if (isChecked) {
                                    updatedDays = [...selectedDays, day.key];
                                  } else {
                                    const dayIndex = selectedDays.indexOf(day.key);
                                    if (dayIndex !== -1) {
                                      updatedDays = [
                                        ...selectedDays.slice(0, dayIndex),
                                        ...selectedDays.slice(dayIndex + 1),
                                      ];
                                    } else {
                                      updatedDays = selectedDays;
                                    }
                                  }

                                  field.onChange(updatedDays);
                                }}
                                sx={{
                                  display: 'grid',
                                  placeItems: 'center',
                                  cursor: 'pointer',
                                  height: '28px',
                                  width: '28px',
                                  fontSize: '14px',
                                  borderRadius: '100%',
                                  color: checked
                                    ? 'var(--dcm-blue-base)'
                                    : 'var(--dcm-gray-general)',
                                  backgroundColor: checked
                                    ? 'var(--dcm-blue-lighter)'
                                    : 'var(--dcm-gray-lightbg)',
                                  userSelect: 'none',
                                }}
                              >
                                {day.label.substring(0, 1)}
                              </Box>
                            </Box>
                          </Tooltip>
                        );
                      }}
                    />
                  ))}
                </FormGroup>

                <Slide
                  direction="left"
                  in={Boolean(errors?.days?.message)}
                  mountOnEnter
                  unmountOnExit
                >
                  <span style={{color: Pallete.red.base, fontSize: '14px'}}>
                    {errors?.days?.message}
                  </span>
                </Slide>
              </Stack>
            </Collapse>
          </Stack>
        </Collapse>
        <Collapse in={!displayForm && type === SchedulerTypes.ActivityType}>
          <Box sx={{width: '100%', display: 'flex', justifyContent: 'center', py: 3}}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '16.41px',
                color: Pallete.gray.light,
              }}
            >
              {t(translations.periodicity_form.empty_state)}
            </Typography>
          </Box>
        </Collapse>
      </form>
    </>
  );
};
